// RITA-1599 — Ob der gelbe Hinweiskasten gerade aus dem sichtbaren Bild gescrollt ist (Entscheidung Julia 03.11.2026:
// nur dann steht die orange Hinweisleiste im festen Titel).
import { RefObject, useEffect, useState } from 'react';

/**
 * `aktiv` = es gibt Hinweise (sonst gibt es weder Kasten noch Leiste). `obenVerdecktPx` ist der Streifen am oberen
 * Fensterrand, den App-Kopf und klebende Titelzeile verdecken: Ein Kasten dahinter gilt als außer Sicht
 * (Review-Befund 1). Mit IntersectionObserver entscheidet dessen erste Meldung – sie kommt immer, auch für
 * „nicht sichtbar“ –, damit die Leiste beim Entstehen des ersten Hinweises im sichtbaren Kasten nicht kurz
 * aufblitzt (Review-Befund 2); der Startwert gilt nur beim Entstehen des Kastens, nicht bei einer Änderung des
 * verdeckten Streifens. Ohne IntersectionObserver (jsdom) gilt der Kasten als außer Sicht, damit die
 * Leiste nie fehlt.
 */
export default function useKastenAusserSicht(
  kastenRef: RefObject<HTMLElement>,
  aktiv: boolean,
  obenVerdecktPx: number,
): boolean {
  const [ausserSicht, setAusserSicht] = useState(false);
  useEffect(() => {
    const kasten = kastenRef.current;
    if (!aktiv || !kasten) {
      setAusserSicht(false);
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setAusserSicht(true);
      return undefined;
    }
    // Ändert sich nur der verdeckte Streifen (die Leiste selbst macht die Titelzeile höher), bleibt der Stand stehen:
    // Ein Zurücksetzen ließe die Leiste bei jedem Erscheinen ein Bild lang verschwinden und könnte schwingen
    // (Review-Nachprüfung N1). Die erste Meldung des neuen Beobachters bestätigt oder korrigiert den Stand.
    const beobachter = new IntersectionObserver(([eintrag]) => setAusserSicht(!eintrag.isIntersecting), {
      rootMargin: `-${Math.max(0, Math.round(obenVerdecktPx))}px 0px 0px 0px`,
    });
    beobachter.observe(kasten);
    return () => beobachter.disconnect();
  }, [kastenRef, aktiv, obenVerdecktPx]);
  return aktiv && ausserSicht;
}
